import { Order, OrderItem, Product } from '../types';
import { vatParts } from './bundles';
import { effectiveVatRate, LEGACY_VAT_RATE, ProductCategory, round2, VatLine, vatIncluded } from './vat';

/**
 * Fakturans moms. Två slags ordrar, med olika momsregler:
 *
 * - Hämtade ordrar där kunden betalat säljaren: gruppen köper varorna av
 *   CoffeeNova. Gruppens provision (bonustrappan) är en rabatt på varorna och minskar
 *   beskattningsunderlaget. Momsen räknas per sats på
 *   försäljning − rabatt.
 * - Ordrar betalda i kassan: CoffeeNova har sålt direkt till kunden och
 *   redovisat momsen på det köpet (momsrapporten, "Betalt i kassan").
 *   Varorna finns inte på fakturan. Gruppens vinst på dem är en provision
 *   som CoffeeNova betalar och som inte påverkar CoffeeNovas utgående moms.
 *
 * Satsen är den som gäller på leveransdagen (då beskattningsgrundande
 * händelsen inträffar), inte den som gällde när kunden beställde. Det spelar
 * roll när en sats ändras under säljperioden, t.ex. matmomsen 2026–2027.
 *
 * Delarna går jämnt upp mot fakturans totaler; ett öresfel efter avrundning
 * läggs på den största satsen.
 */
export interface InvoiceVatRow {
    rate: number;
    /** Varor gruppen köpt (hämtade ordrar), inklusive moms. */
    sales: number;
    /** Rabatt: gruppens vinst på de varorna. */
    discount: number;
    /** Beskattningsunderlag inklusive moms = sales − discount. */
    toPay: number;
    vat: number;
    net: number;
}

export interface InvoiceVat {
    rows: InvoiceVatRow[];
    /** Gruppens provision på köp betalda i kassan. Ingen moms. */
    onlineProvision: number;
}

/** Satsen för en orderrad på leveransdagen: produktens kategori, annars satsen ordern fick. */
export const deliveryVatRate = (products: Product[], categories: ProductCategory[], deliveryDate: Date) => {
    const productById = new Map(products.map(p => [p.id, p]));
    const categoryById = new Map(categories.map(c => [c.id, c]));
    // Gäller både orderrader och paketens delar (samma fält).
    return (item: { productId: string; vatRate?: number | null }): number => {
        const categoryId = productById.get(item.productId)?.categoryId;
        const category = categoryId ? categoryById.get(categoryId) : undefined;
        return category ? effectiveVatRate(category, deliveryDate) : item.vatRate ?? LEGACY_VAT_RATE;
    };
};

const snapshotRate = (item: { vatRate?: number | null }) => item.vatRate ?? LEGACY_VAT_RATE;


/**
 * commissionOf: gruppens provision per orderrad enligt bonussystemet
 * (itemCommissionOf i services/bonus.ts). totalClassProfit: provisionen
 * totalt, som delarna rättas mot.
 */
export const invoiceVat = (
    orders: Order[],
    commissionOf: (item: OrderItem) => number,
    totalClassProfit: number,
    rateOf: (item: { productId: string; vatRate?: number | null }) => number = snapshotRate
): InvoiceVat => {
    const rows = new Map<number, { sales: number; discount: number }>();
    let onlineProvision = 0;
    for (const order of orders) {
        const online = order.paymentMethod === 'STRIPE';
        for (const item of order.items) {
            const profit = commissionOf(item);
            if (online) {
                onlineProvision += profit;
                continue;
            }
            // Paket: varje del har sin sats, och provisionen delas efter delens andel.
            for (const part of vatParts(item, rateOf)) {
                const row = rows.get(part.rate) || { sales: 0, discount: 0 };
                row.sales += part.amount;
                row.discount += profit * part.share;
                rows.set(part.rate, row);
            }
        }
    }

    // Avrunda och lägg öresskillnaden mot gruppens totala vinst på den största satsen.
    onlineProvision = round2(onlineProvision);
    let sum = onlineProvision;
    for (const r of rows.values()) {
        r.sales = round2(r.sales);
        r.discount = round2(r.discount);
        sum = round2(sum + r.discount);
    }
    const diff = round2(round2(totalClassProfit) - sum);
    if (diff !== 0) {
        const largest = [...rows.entries()].sort((a, b) => b[1].sales - a[1].sales || b[0] - a[0])[0];
        if (largest) largest[1].discount = round2(largest[1].discount + diff);
        else onlineProvision = round2(onlineProvision + diff);
    }

    return {
        onlineProvision,
        rows: [...rows.entries()]
            .map(([rate, r]) => {
                const toPay = round2(r.sales - r.discount);
                const vat = vatIncluded(toPay, rate);
                return { rate, sales: r.sales, discount: r.discount, toPay, vat, net: round2(toPay - vat) };
            })
            .sort((a, b) => a.rate - b.rate),
    };
};

/** Som VatLine (samma form som ordrarnas moms), för fakturans sammanställning. */
export const invoiceVatLines = (rows: InvoiceVatRow[]): VatLine[] =>
    rows.map(r => ({ rate: r.rate, gross: r.toPay, shipping: 0, vat: r.vat, net: r.net }));

/** Enhetspris exklusive moms, krav på fullständig faktura. */
export const priceExVat = (gross: number, rate: number) => round2((gross * 100) / (100 + rate));
